/**
 * Акты на дополнительные работы с площадки (ПР-20): прораб заводит, заказчик или технадзор подписывает —
 * появляется запрос на изменение. Каркас без полного экрана отчёта смены.
 */
import type {
  CustomerConnection,
  ExtraWorkActDetail,
  ExtraWorkActSummary,
  ProjectSummary,
} from '@epc/contracts';
import { EXTRA_WORK_ACT_STATUSES, EXTRA_WORK_SIGNER_KINDS } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';

type Search = { project?: string; day?: string };

const todayStr = () => new Date().toLocaleDateString('en-CA');

export function ExtraWorksListPage() {
  const me = useMe();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Search;
  const day = search.day;
  useCrumbs([{ label: 'Площадка', to: '/site' }, { label: 'Акты допработ' }]);
  const projects = useQuery({
    queryKey: ['projects', me.tenant?.id, 'extra-works'],
    queryFn: () => api<ProjectSummary[]>('/projects'),
    enabled: Boolean(me.tenant),
  });
  const projectId = search.project ?? projects.data?.[0]?.id;
  const list = useQuery({
    queryKey: ['site', me.tenant?.id, 'extra-work-acts', projectId, day],
    queryFn: () =>
      api<ExtraWorkActSummary[]>(`/site/projects/${projectId}/extra-work-acts`, {
        query: day ? { day } : {},
      }),
    enabled: Boolean(me.tenant && projectId),
  });
  const staff = Boolean(me.tenant?.staff);
  const qc = useQueryClient();
  const connKey = ['site', me.tenant?.id, 'customer-connection', projectId];
  const conn = useQuery({
    queryKey: connKey,
    queryFn: () => api<CustomerConnection>(`/site/projects/${projectId}/customer-connection`),
    enabled: Boolean(me.tenant && projectId && staff),
  });
  const setConn = useMutation({
    mutationFn: (connected: boolean) =>
      api<CustomerConnection>(`/site/projects/${projectId}/customer-connection`, {
        method: 'PUT',
        body: { connected },
      }),
    onSuccess: (c) => qc.setQueryData(connKey, c),
  });

  if (!me.tenant)
    return (
      <Page>
        <ErrorBox error={new Error('Выберите компанию')} />
      </Page>
    );

  return (
    <Page>
      <PageHeader
        title="Акты на допработы"
        subtitle="Работы сверх договора: подпись заказчика или технадзора создаёт запрос на изменение"
        actions={
          staff && projectId ? (
            <Button
              type="button"
              className="min-h-11"
              onClick={() =>
                navigate({
                  to: '/site/extra-works/new',
                  search: { project: projectId, day: day ?? todayStr() } as never,
                })
              }
            >
              Новый акт
            </Button>
          ) : null
        }
      />
      <div className="mb-4 flex flex-wrap gap-3">
        <Field label="Проект">
          <Select
            className="min-h-11"
            value={projectId ?? ''}
            onChange={(e) =>
              navigate({
                to: '/site/extra-works',
                search: { project: e.target.value || undefined, day } as never,
              })
            }
          >
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} — {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="День">
          <Input
            type="date"
            className="min-h-11"
            value={day ?? ''}
            onChange={(e) =>
              navigate({
                to: '/site/extra-works',
                search: { project: projectId, day: e.target.value || undefined } as never,
              })
            }
          />
        </Field>
      </div>
      {staff && conn.data ? (
        <Panel title="Заказчик в портале" className="mb-4">
          <p className="text-[14px] text-ink-2">
            {conn.data.connected
              ? `Заказчик подключён${conn.data.connectedAt ? ` с ${new Date(conn.data.connectedAt).toLocaleDateString('ru-RU')}` : ''}${conn.data.connectedByName ? ` (отметил ${conn.data.connectedByName})` : ''}. Заказчик и технадзор видят акты и подписывают их в кабинете.`
              : 'Заказчик ещё не подключён: заказчик и технадзор акты не видят и ничего не получают. Отметьте подключение, когда у заказчика есть доступ к порталу.'}
          </p>
          {setConn.error ? <ErrorBox error={setConn.error} className="mt-3" /> : null}
          <Button
            type="button"
            variant={conn.data.connected ? 'ghost' : 'primary'}
            className="mt-3 min-h-11"
            disabled={setConn.isPending}
            onClick={() => setConn.mutate(!conn.data!.connected)}
          >
            {conn.data.connected ? 'Снять отметку' : 'Заказчик подключён'}
          </Button>
        </Panel>
      ) : null}
      {list.isLoading ? <Spinner /> : null}
      {list.error ? <ErrorBox error={list.error} /> : null}
      {list.data && list.data.length === 0 ? (
        <EmptyState title="Актов пока нет">Прораб заводит акт за день на работы сверх договора.</EmptyState>
      ) : null}
      {list.data && list.data.length > 0 ? (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
          {list.data.map((a) => (
            <li key={a.id}>
              <Link
                to="/site/extra-works/$actId"
                params={{ actId: a.id }}
                className="flex min-h-11 flex-col gap-1 px-4 py-3 hover:bg-surface-2 md:flex-row md:items-center md:justify-between"
              >
                <span className="font-medium text-ink">
                  {a.number} · {a.title}
                </span>
                <span className="flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
                  <Badge>{EXTRA_WORK_ACT_STATUSES[a.status]}</Badge>
                  <span>{a.actDay}</span>
                  {a.changeNumber ? <span>→ {a.changeNumber}</span> : null}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </Page>
  );
}

export function ExtraWorkNewPage() {
  const me = useMe();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Search;
  const projectId = search.project;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [actDay, setActDay] = useState(search.day ?? todayStr());
  const [activityId, setActivityId] = useState('');
  const [contractId, setContractId] = useState('');
  const [laborNote, setLaborNote] = useState('');
  const [machinesNote, setMachinesNote] = useState('');
  const [materialsNote, setMaterialsNote] = useState('');
  useCrumbs([
    { label: 'Площадка', to: '/site' },
    { label: 'Акты допработ', to: '/site/extra-works' },
    { label: 'Новый акт' },
  ]);
  const activities = useQuery({
    queryKey: ['site', me.tenant?.id, 'day-plan', projectId, actDay],
    queryFn: () =>
      api<{
        packages: Array<{
          lines: Array<{ activityId: string; name: string }>;
        }>;
      }>(`/site/projects/${projectId}/day-plan`, { query: { date: actDay } }),
    enabled: Boolean(me.tenant?.staff && projectId),
  });
  const contracts = useQuery({
    queryKey: ['contracts', me.tenant?.id, projectId],
    queryFn: () =>
      api<Array<{ id: string; number: string; kind: string; status: string }>>('/contracts', {
        query: { projectId },
      }),
    enabled: Boolean(me.tenant?.staff && projectId),
  });
  const save = useMutation({
    mutationFn: () =>
      api<ExtraWorkActDetail>('/site/extra-work-acts', {
        method: 'POST',
        body: {
          projectId: projectId!,
          actDay,
          title,
          description,
          activityId,
          contractId,
          laborNote: laborNote || null,
          machinesNote: machinesNote || null,
          materialsNote: materialsNote || null,
        },
      }),
    onSuccess: (a) => navigate({ to: '/site/extra-works/$actId', params: { actId: a.id } }),
  });
  const acts =
    activities.data?.packages
      .flatMap((p) => p.lines)
      .filter((x, i, all) => all.findIndex((y) => y.activityId === x.activityId) === i) ?? [];

  if (!me.tenant?.staff)
    return (
      <Page>
        <ErrorBox error={new Error('Акт заводит сотрудник площадки')} />
      </Page>
    );
  if (!projectId)
    return (
      <Page>
        <ErrorBox error={new Error('Выберите проект в списке актов')} />
      </Page>
    );

  return (
    <Page>
      <PageHeader
        title="Новый акт на допработы"
        subtitle="Люди, техника и материалы на работах сверх договора"
        actions={
          <Button
            type="button"
            variant="ghost"
            className="min-h-11"
            onClick={() => navigate({ to: '/site/extra-works', search: { project: projectId } as never })}
          >
            <ChevronLeft className="size-4" /> Назад
          </Button>
        }
      />
      <Panel title="Акт">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="День">
            <Input
              type="date"
              className="min-h-11"
              value={actDay}
              onChange={(e) => setActDay(e.target.value)}
            />
          </Field>
          <Field label="Кратко">
            <Input
              className="min-h-11"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Что сделали сверх договора"
            />
          </Field>
          <Field label="Работа графика" className="md:col-span-2">
            <Select className="min-h-11" value={activityId} onChange={(e) => setActivityId(e.target.value)}>
              <option value="">Выберите работу</option>
              {acts.map((a) => (
                <option key={a.activityId} value={a.activityId}>
                  {a.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Договор" className="md:col-span-2">
            <Select className="min-h-11" value={contractId} onChange={(e) => setContractId(e.target.value)}>
              <option value="">Выберите договор</option>
              {(contracts.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.number}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Описание" className="md:col-span-2">
            <Textarea
              className="min-h-24"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <Field label="Люди">
            <Textarea
              className="min-h-20"
              value={laborNote}
              onChange={(e) => setLaborNote(e.target.value)}
              placeholder="Кто работал"
            />
          </Field>
          <Field label="Техника">
            <Textarea
              className="min-h-20"
              value={machinesNote}
              onChange={(e) => setMachinesNote(e.target.value)}
              placeholder="Какая техника"
            />
          </Field>
          <Field label="Материалы" className="md:col-span-2">
            <Textarea
              className="min-h-20"
              value={materialsNote}
              onChange={(e) => setMaterialsNote(e.target.value)}
              placeholder="Что израсходовали"
            />
          </Field>
        </div>
        {save.error ? <ErrorBox error={save.error} className="mt-4" /> : null}
        <div className="mt-4">
          <Button
            type="button"
            className="min-h-11"
            disabled={save.isPending || !title.trim() || !description.trim() || !activityId || !contractId}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Сохраняю…' : 'Сохранить черновик'}
          </Button>
        </div>
      </Panel>
    </Page>
  );
}

export function ExtraWorkActPage() {
  const me = useMe();
  const { actId } = useParams({ strict: false }) as { actId: string };
  const qc = useQueryClient();
  useCrumbs([
    { label: 'Площадка', to: '/site' },
    { label: 'Акты допработ', to: '/site/extra-works' },
    { label: 'Акт' },
  ]);
  const q = useQuery({
    queryKey: ['site', me.tenant?.id, 'extra-work-act', actId],
    queryFn: () => api<ExtraWorkActDetail>(`/site/extra-work-acts/${actId}`),
    enabled: Boolean(me.tenant && actId),
  });
  const sign = useMutation({
    mutationFn: () =>
      api<ExtraWorkActDetail>(`/site/extra-work-acts/${actId}/sign`, {
        method: 'POST',
        body: { expectedHash: q.data?.contentHash ?? undefined },
      }),
    onSuccess: (a) => {
      void qc.setQueryData(['site', me.tenant?.id, 'extra-work-act', actId], a);
      void qc.invalidateQueries({ queryKey: ['site', me.tenant?.id, 'extra-work-acts'] });
    },
  });
  const [comment, setComment] = useState('');
  const [fix, setFix] = useState<{ title: string; description: string; machines: string } | null>(null);
  const back = useMutation({
    mutationFn: () =>
      api<ExtraWorkActDetail>(`/site/extra-work-acts/${actId}/return`, {
        method: 'POST',
        body: { comment },
      }),
    onSuccess: (a) => {
      setComment('');
      void qc.setQueryData(['site', me.tenant?.id, 'extra-work-act', actId], a);
      void qc.invalidateQueries({ queryKey: ['site', me.tenant?.id, 'extra-work-acts'] });
    },
  });
  const patch = useMutation({
    mutationFn: () => {
      const x = q.data!;
      return api<ExtraWorkActDetail>(`/site/extra-work-acts/${actId}`, {
        method: 'PATCH',
        body: {
          objectId: x.objectId,
          actDay: x.actDay,
          title: fix!.title,
          description: fix!.description,
          activityId: x.activityId,
          contractId: x.contractId,
          laborNote: x.laborNote,
          machinesNote: fix!.machines || null,
          materialsNote: x.materialsNote,
        },
      });
    },
    onSuccess: (a) => {
      setFix(null);
      void qc.setQueryData(['site', me.tenant?.id, 'extra-work-act', actId], a);
      void qc.invalidateQueries({ queryKey: ['site', me.tenant?.id, 'extra-work-acts'] });
    },
  });
  const a = q.data;

  return (
    <Page>
      <PageHeader
        title={a ? `${a.number} · ${a.title}` : 'Акт на допработы'}
        subtitle={a ? `${a.projectCode} · ${a.actDay}` : undefined}
        actions={
          <Link
            to="/site/extra-works"
            className="inline-flex min-h-11 items-center gap-1 text-ink-2 hover:text-ink"
          >
            <ChevronLeft className="size-4" /> К списку
          </Link>
        }
      />
      {q.isLoading ? <Spinner /> : null}
      {q.error ? <ErrorBox error={q.error} /> : null}
      {a ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="Акт" meta={EXTRA_WORK_ACT_STATUSES[a.status]}>
            <dl className="grid gap-3 text-[14px]">
              <div>
                <dt className="text-ink-2">Работа</dt>
                <dd>
                  {a.activityCode} «{a.activityName}»
                </dd>
              </div>
              <div>
                <dt className="text-ink-2">Договор</dt>
                <dd>
                  <Link
                    to="/contracts/$contractId"
                    params={{ contractId: a.contractId }}
                    className="inline-flex min-h-11 items-center underline-offset-4 hover:underline md:min-h-0"
                  >
                    {a.contractNumber}
                  </Link>
                </dd>
              </div>
              <div>
                <dt className="text-ink-2">Описание</dt>
                <dd className="whitespace-pre-wrap">{a.description}</dd>
              </div>
              {a.laborNote ? (
                <div>
                  <dt className="text-ink-2">Люди</dt>
                  <dd className="whitespace-pre-wrap">{a.laborNote}</dd>
                </div>
              ) : null}
              {a.machinesNote ? (
                <div>
                  <dt className="text-ink-2">Техника</dt>
                  <dd className="whitespace-pre-wrap">{a.machinesNote}</dd>
                </div>
              ) : null}
              {a.materialsNote ? (
                <div>
                  <dt className="text-ink-2">Материалы</dt>
                  <dd className="whitespace-pre-wrap">{a.materialsNote}</dd>
                </div>
              ) : null}
            </dl>
          </Panel>
          <Panel title="Подпись и изменение">
            {a.status === 'signed' ? (
              <div className="space-y-3 text-[14px]">
                <p>
                  Подписан
                  {a.signerKind ? ` (${EXTRA_WORK_SIGNER_KINDS[a.signerKind]})` : ''}
                  {a.signedByName ? ` — ${a.signedByName}` : ''}
                  {a.signedAt ? `, ${new Date(a.signedAt).toLocaleString('ru-RU')}` : ''}.
                </p>
                {a.contentHash ? (
                  <p className="break-all text-[13px] text-ink-2">
                    Контрольная сумма содержания (sha256): <span className="font-mono">{a.contentHash}</span>.
                    Если в акте что-то изменят, сумма перестанет совпадать.
                  </p>
                ) : (
                  <p className="text-[13px] text-ink-2">Подписан до появления контрольной суммы.</p>
                )}
                {a.changeId && a.changeNumber ? (
                  <p>
                    Запрос на изменение:{' '}
                    <Link
                      to="/changes/$changeId"
                      params={{ changeId: a.changeId }}
                      className="inline-flex min-h-11 items-center font-medium underline-offset-4 hover:underline md:min-h-0"
                    >
                      {a.changeNumber}
                    </Link>
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="space-y-4">
                {a.returnComment ? (
                  <div className="rounded-md border border-line bg-surface-2 p-3 text-[14px]">
                    <p className="font-medium text-ink">
                      {a.status === 'returned' ? 'Возвращён с замечанием' : 'Раньше возвращали с замечанием'}
                      {a.returnedByName ? ` — ${a.returnedByName}` : ''}
                      {a.returnedAt ? `, ${new Date(a.returnedAt).toLocaleString('ru-RU')}` : ''}
                    </p>
                    <p className="mt-1 whitespace-pre-wrap">{a.returnComment}</p>
                  </div>
                ) : null}
                {a.canSign ? (
                  <div className="space-y-3">
                    <p className="text-[14px] text-ink-2">
                      Подпись фиксирует, кто и когда подписал и контрольную сумму содержания; портал создаст
                      запрос на изменение за счёт заказчика с этим актом как основанием.
                    </p>
                    {sign.error ? <ErrorBox error={sign.error} /> : null}
                    <Button
                      type="button"
                      className="min-h-11"
                      disabled={sign.isPending}
                      onClick={() => sign.mutate()}
                    >
                      {sign.isPending ? 'Подписываю…' : 'Подписать акт'}
                    </Button>
                  </div>
                ) : a.status === 'returned' ? (
                  <p className="text-[14px] text-ink-2">Ждёт исправления прорабом.</p>
                ) : (
                  <p className="text-[14px] text-ink-2">
                    Ждёт подписи представителя заказчика или технадзора в кабинете.
                  </p>
                )}
                {a.canReturn ? (
                  <div className="space-y-2">
                    <Field label="Вернуть прорабу с замечанием">
                      <Textarea
                        className="min-h-20"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Что исправить в акте"
                      />
                    </Field>
                    {back.error ? <ErrorBox error={back.error} /> : null}
                    <Button
                      type="button"
                      variant="ghost"
                      className="min-h-11"
                      disabled={back.isPending || comment.trim().length < 3}
                      onClick={() => back.mutate()}
                    >
                      {back.isPending ? 'Возвращаю…' : 'Вернуть с замечанием'}
                    </Button>
                  </div>
                ) : null}
                {a.canEdit ? (
                  fix ? (
                    <div className="space-y-2">
                      <Field label="Кратко">
                        <Input
                          className="min-h-11"
                          value={fix.title}
                          onChange={(e) => setFix({ ...fix, title: e.target.value })}
                        />
                      </Field>
                      <Field label="Описание">
                        <Textarea
                          className="min-h-24"
                          value={fix.description}
                          onChange={(e) => setFix({ ...fix, description: e.target.value })}
                        />
                      </Field>
                      <Field label="Техника">
                        <Textarea
                          className="min-h-20"
                          value={fix.machines}
                          onChange={(e) => setFix({ ...fix, machines: e.target.value })}
                        />
                      </Field>
                      {patch.error ? <ErrorBox error={patch.error} /> : null}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          className="min-h-11"
                          disabled={
                            patch.isPending ||
                            fix.title.trim().length < 3 ||
                            fix.description.trim().length < 3
                          }
                          onClick={() => patch.mutate()}
                        >
                          {patch.isPending ? 'Сохраняю…' : 'Сохранить и отправить на подпись'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          className="min-h-11"
                          onClick={() => setFix(null)}
                        >
                          Отмена
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      className="min-h-11"
                      onClick={() =>
                        setFix({ title: a.title, description: a.description, machines: a.machinesNote ?? '' })
                      }
                    >
                      Исправить акт
                    </Button>
                  )
                ) : null}
              </div>
            )}
          </Panel>
        </div>
      ) : null}
    </Page>
  );
}
